import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, Typography, Paper, CircularProgress } from '@material-ui/core';
import LockIcon from '@material-ui/icons/Lock';
import { RMSButton } from '../../shared/components/buttons';
import { executeApiAction } from '../../shared/utility/apiHelper';
import { buildShareBlobUrl } from '../constants/shareLinkConsts';

const renderPreview = (share, previewUrl) => {
  if (!previewUrl || !share) return null;

  if (share.type.startsWith('image/')) {
    return <img src={previewUrl} alt={share.fileName} className="public-share-preview-image" />;
  }

  if (share.type.startsWith('video/')) {
    return (
      <video controls src={previewUrl} className="public-share-preview-video">
        Your browser does not support video playback.
      </video>
    );
  }

  if (share.type.startsWith('audio/')) {
    return <audio controls src={previewUrl} className="public-share-preview-audio" />;
  }

  if (share.type === 'application/pdf') {
    return <iframe title={share.fileName} src={previewUrl} className="public-share-preview-pdf" />;
  }

  return (
    <Typography variant="body2" className="public-share-no-preview">
      No preview available for this file type.
    </Typography>
  );
};

const PageShareView = () => {
  const { token } = useParams();
  const dispatch = useDispatch();
  const apiUrl = window.__RUNTIME_CONFIG__.REACT_APP_API_URL;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [share, setShare] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const fetchShare = useCallback(
    async (download = false) => {
      const response = await fetch(
        `${apiUrl}/public-attachment-share/${token}${download ? '?download=1' : ''}`
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'Unable to load this file.');
      }

      return data;
    },
    [apiUrl, token]
  );

  useEffect(() => {
    let objectUrl;

    executeApiAction({
      setLoading,
      dispatch,
      action: () => fetchShare(false),
      onSuccess: (data) => {
        objectUrl = buildShareBlobUrl(data);
        setShare(data);
        setPreviewUrl(objectUrl);
      },
      onError: (err) => setError(err.message),
    });

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fetchShare, dispatch]);

  const handleDownload = async () => {
    await executeApiAction({
      setLoading: setDownloading,
      dispatch,
      action: () => fetchShare(true),
      onSuccess: (data) => {
        const url = buildShareBlobUrl(data);
        const link = document.createElement('a');

        link.href = url;
        link.download = data.fileName;
        link.click();
        URL.revokeObjectURL(url);
      },
      onError: (err) => setError(err.message),
    });
  };

  return (
    <Box className="public-share-page">
      <Paper elevation={0} className="public-share-card">
        <Box className="public-share-header">
          <LockIcon className="public-share-header-icon" />
          <Typography variant="h6">PTS Evidence Management — Shared File</Typography>
        </Box>

        <Box className="public-share-body">
          {loading && (
            <Box className="public-share-loading">
              <CircularProgress size={28} />
              <Typography variant="body2">Loading shared file…</Typography>
            </Box>
          )}

          {!loading && error && (
            <Box className="public-share-error">
              <Typography variant="subtitle1">This link can&apos;t be opened</Typography>
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}

          {!loading && !error && share && (
            <>
              <Typography variant="subtitle1" className="public-share-filename">
                {share.fileName}
              </Typography>

              <Typography variant="caption" className="public-share-expiry">
                Link expires {new Date(share.expiresAt).toLocaleString()}
              </Typography>

              <Box className="public-share-preview">{renderPreview(share, previewUrl)}</Box>

              {share.allowDownload && (
                <RMSButton
                  label={downloading ? 'Preparing…' : 'Download'}
                  onClick={handleDownload}
                  disabled={downloading}
                />
              )}
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default PageShareView;
